import { EnterForm } from '../components/EnterForm';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFY, isDateInFY, getFYDateError } from '../context/FYContext';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import { getIncome, createIncome, updateIncome, deleteIncome, bulkDeleteIncome, getIncomeReport, getAllUsersBasic, getEntryLedgers } from '../lib/api';
import { formatCurrency, formatDate, getToday, getDateRange, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { Plus, Pencil, Trash2, TrendingUp, AlertCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { DateRangeFilter } from '../components/ui/date-range-filter';
import { toast } from 'sonner';

const DEFAULT_INCOME_CATEGORIES = ['Sales', 'Investment', 'Transfer', 'Others'];

const Income = () => {
  const { user, isAdmin } = useAuth();
  const { selectedFY, fyStartDate, fyEndDate, shortLabel: fyLabel } = useFY();
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_INCOME_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const toggleExpand = (id) => setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [users, setUsers] = useState([]);
  const [entryLedgers, setEntryLedgers] = useState([]);

  const currentMonth = getDateRange('current-month');
  const [dateRange, setDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [activeQuick, setActiveQuick] = useState('current-month');

  const [incomeReport, setIncomeReport] = useState(null);

  const [formData, setFormData] = useState({ date: getToday(), amount: '', category: '', remarks: '', user_id: '', entry_ledger_id: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(dateRange.start_date, dateRange.end_date); }, []);

  useEffect(() => {
    if (isAdmin) { getAllUsersBasic().then(res => setUsers(res.data)).catch(() => {}); }
    getEntryLedgers().then(res => setEntryLedgers(res.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (location.state?.openDialog) { openCreateDialog(); window.history.replaceState({}, document.title); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchData = async (startDate, endDate) => {
    setLoading(true);
    setSelectedIds([]);
    try {
      const [res, reportRes] = await Promise.all([
        getIncome(startDate, endDate),
        getIncomeReport(startDate, endDate)
      ]);
      setEntries(res.data);
      setIncomeReport(reportRes.data);
      setCategories([...new Set([...DEFAULT_INCOME_CATEGORIES, ...res.data.map(e => e.category).filter(Boolean)])]);
    } catch (err) { toast.error('Failed to fetch data'); } finally { setLoading(false); }
  };

  const handleDateApply = (s, e) => { setDateRange({ start_date: s, end_date: e }); fetchData(s, e); };
  const resetForm = () => { setFormData({ date: getToday(), amount: '', category: '', remarks: '', user_id: '', entry_ledger_id: '' }); setEditingEntry(null); setError(''); };
  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };

  usePageCallbacks({
    onNewEntry: openCreateDialog,
    onDateQuick: (key) => {
      const range = getDateRange(key);
      setDateRange({ start_date: range.start_date, end_date: range.end_date });
      setActiveQuick(key);
      fetchData(range.start_date, range.end_date);
    },
    onDateCustom: () => {
      setActiveQuick('');
      const el = document.querySelector('[data-testid="income-date-start"]');
      if (el) el.focus();
    },
  });
  const openEditDialog = (entry) => { setEditingEntry(entry); setFormData({ date: entry.date, amount: entry.amount.toString(), category: entry.category, remarks: entry.remarks || '', user_id: entry.user_id || '', entry_ledger_id: entry.entry_ledger_id || '' }); setDialogOpen(true); };
  const handleCreateCategory = (cat) => { setCategories(prev => [...prev, cat]); setFormData(prev => ({ ...prev, category: cat })); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    if (!isDateInFY(formData.date, selectedFY)) { setError(getFYDateError(selectedFY, fyLabel)); setSubmitting(false); return; }
    const payload = { date: formData.date, amount: parseFloat(formData.amount), category: formData.category, remarks: formData.remarks, user_id: isAdmin ? formData.user_id : user.id, entry_ledger_id: formData.entry_ledger_id || undefined };
    try {
      if (editingEntry) { await updateIncome(editingEntry.id, payload); toast.success('Updated'); }
      else { await createIncome(payload); toast.success('Created'); }
      setDialogOpen(false); resetForm(); fetchData(dateRange.start_date, dateRange.end_date);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await deleteIncome(entry.id); toast.success('Deleted'); fetchData(dateRange.start_date, dateRange.end_date); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const canDelete = (entry) => {
    if (isAdmin) return true;
    if (entry.user_id !== user.id) return false;
    return Math.floor((new Date() - new Date(entry.date)) / 86400000) <= 7;
  };

  const canEdit = (entry) => {
    if (isAdmin) return true;
    if (entry.user_id !== user.id) return false;
    return Math.floor((new Date() - new Date(entry.date)) / 86400000) <= 7;
  };

  // Selection logic
  const deletableEntries = entries.filter(e => canDelete(e));
  const allSelected = deletableEntries.length > 0 && deletableEntries.every(e => selectedIds.includes(e.id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(deletableEntries.map(e => e.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected entries?`)) return;
    try {
      const res = await bulkDeleteIncome(selectedIds);
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchData(dateRange.start_date, dateRange.end_date);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const totalIncome = entries.reduce((s, e) => s + e.amount, 0);
  const { sortedData: sortedEntries, sortKey, sortDirection, handleSort } = useSortableTable(entries, 'date', 'asc');
  const categoryOptions = categories.map(c => ({ value: c, label: c }));

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Income</h1>
          <p className="text-sm text-muted-foreground hidden sm:block">Manage income entries</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} data-testid="add-income-button"><Plus className="h-4 w-4 mr-2" />Add Income</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingEntry ? 'Edit Income' : 'Add Income'}</DialogTitle></DialogHeader>
            <EnterForm onSubmit={handleSubmit} className="space-y-4">
              {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Date</Label><DateInput fyStartYear={selectedFY} value={formData.date} onChange={(e) => { const v = e.target.value; setFormData({...formData, date: v}); if (v && !isDateInFY(v, selectedFY)) toast.error(getFYDateError(selectedFY, fyLabel)); }} required data-testid="income-date-input" /></div>
                <div className="space-y-2"><Label>Amount</Label><Input type="number" step="0.01" min="0" placeholder="0.00" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required data-testid="income-amount-input" /></div>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <SearchableSelect options={categoryOptions} value={formData.category} onChange={(v) => setFormData({...formData, category: v})} onCreateNew={handleCreateCategory} placeholder="Select or type category" searchPlaceholder="Search or create..." createNewLabel="Create category" data-testid="income-category-select" />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label>User</Label>
                  <SearchableSelect options={users.map(u => ({ value: u.id, label: u.name }))} value={formData.user_id} onChange={(v) => setFormData({...formData, user_id: v})} placeholder="Select user" searchPlaceholder="Search user..." data-testid="income-user-select" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Entry Ledger (Optional)</Label>
                <SearchableSelect options={[{ value: '', label: 'None' }, ...entryLedgers.map(l => ({ value: l.id, label: `${l.name}${l.category ? ` (${l.category})` : ''}` }))]} value={formData.entry_ledger_id} onChange={(v) => setFormData({...formData, entry_ledger_id: v})} placeholder="Link to entry ledger" searchPlaceholder="Search ledger..." data-testid="income-entry-ledger-select" />
              </div>
              <div className="space-y-2"><Label>Remarks</Label><Textarea placeholder="Optional remarks..." value={formData.remarks} onChange={(e) => setFormData({...formData, remarks: e.target.value})} data-testid="income-remarks-input" /></div>
              <DialogFooter>
                <FormShortcutHints shortcuts={[{key: '⇧+Enter', label: 'New line'}]} />
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting || !formData.category || (isAdmin && !formData.user_id)} data-testid="income-submit-button">{submitting ? 'Saving...' : editingEntry ? 'Update' : 'Add'}</Button>
              </DialogFooter>
            </EnterForm>
          </DialogContent>
        </Dialog>
      </div>

      {/* Date filter row + Total */}
      <div className="flex flex-col gap-2 sm:gap-3">
        <div className="flex-1">
          <DateRangeFilter startDate={dateRange.start_date} endDate={dateRange.end_date} onStartDateChange={(v) => setDateRange(p => ({...p, start_date: v}))} onEndDateChange={(v) => setDateRange(p => ({...p, end_date: v}))} onApply={handleDateApply} activeQuick={activeQuick} onQuickSelect={setActiveQuick} data-testid="income-date" />
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-card shrink-0">
          <TrendingUp className="h-4 w-4 text-income" />
          <span className="text-sm text-muted-foreground">Total:</span>
          <span className="text-base sm:text-lg font-bold font-mono text-income" data-testid="total-income">{formatCurrency(totalIncome)}</span>
          <span className="text-xs text-muted-foreground">({entries.length})</span>
        </div>
      </div>

      {/* Floating Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds([])} data-testid="clear-selection-btn">Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
            <Trash2 className="h-4 w-4 mr-1" />Delete selected
          </Button>
        </div>
      )}

      {/* Category Breakdown */}
      {incomeReport && (incomeReport.by_category || incomeReport.summary || []).length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-right text-xs">Total</TableHead>
                    <TableHead className="text-right text-xs">Count</TableHead>
                    <TableHead className="text-right text-xs">Avg</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(incomeReport.by_category || incomeReport.summary || []).map((cat) => (
                    <TableRow key={cat.category}>
                      <TableCell className="text-sm">{cat.category}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-income">{formatCurrency(cat.total)}</TableCell>
                      <TableCell className="text-right text-sm">{cat.count}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(cat.total / cat.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 cursor-pointer hidden sm:table-cell" onClick={toggleSelectAll}>
                    <Checkbox checked={allSelected} className="pointer-events-none" data-testid="select-all-income" aria-label="Select all" />
                  </TableHead>
                  <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  {isAdmin && <TableHead className="hidden sm:table-cell"><SortableHeader label="User" sortKey="user_name" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>}
                  <TableHead><SortableHeader label="Category" sortKey="category" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="hidden sm:table-cell"><SortableHeader label="Ledger" sortKey="entry_ledger_name" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="text-right"><SortableHeader label="Amount" sortKey="amount" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                  <TableHead className="hidden sm:table-cell">Remarks</TableHead>
                  <TableHead className="text-right w-[70px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8"><Skeleton className="h-6 w-32 mx-auto" /></TableCell></TableRow>
                ) : entries.length === 0 ? (
                  <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">No income entries found</TableCell></TableRow>
                ) : sortedEntries.map(entry => {
                  const isExpanded = expandedIds.includes(entry.id);
                  return (
                  <React.Fragment key={entry.id}>
                  <TableRow data-testid={`income-row-${entry.id}`} className={`${selectedIds.includes(entry.id) ? 'bg-muted/50' : ''} sm:cursor-default cursor-pointer`} onClick={() => toggleExpand(entry.id)}>
                    <TableCell className={`hidden sm:table-cell ${canDelete(entry) ? 'cursor-pointer' : ''}`} onClick={(e) => { e.stopPropagation(); canDelete(entry) && toggleSelect(entry.id); }}>
                      {canDelete(entry) ? (
                        <Checkbox checked={selectedIds.includes(entry.id)} className="pointer-events-none" data-testid={`select-income-${entry.id}`} />
                      ) : <div className="h-4 w-4" />}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="sm:hidden mr-1 text-muted-foreground">{isExpanded ? '▾' : '▸'}</span>
                      {formatDate(entry.date)}
                    </TableCell>
                    {isAdmin && <TableCell className="hidden sm:table-cell text-sm">{entry.user_name || '-'}</TableCell>}
                    <TableCell><Badge variant="secondary" className="text-xs">{entry.category}</Badge></TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{entry.entry_ledger_name || '-'}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-income">{formatCurrency(entry.amount)}</TableCell>
                    <TableCell className="hidden sm:table-cell max-w-[200px] truncate">{entry.remarks || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEdit(entry) && <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={(e) => { e.stopPropagation(); openEditDialog(entry); }} data-testid={`edit-income-${entry.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                        {canDelete(entry) && <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(entry); }} data-testid={`delete-income-${entry.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="sm:hidden bg-muted/20">
                      <TableCell colSpan={4} className="py-2 px-4">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {isAdmin && <><span className="text-muted-foreground">User</span><span className="text-right">{entry.user_name || '-'}</span></>}
                          <span className="text-muted-foreground">Ledger</span>
                          <span className="text-right">{entry.entry_ledger_name || '-'}</span>
                          {entry.remarks && <><span className="text-muted-foreground">Remarks</span><span className="text-right">{entry.remarks}</span></>}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Income;
